import { useMemo, useState } from 'react';
import { Pencil, Search, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { assegnazioneCompleta } from '@eventi/assegna.ts';
import { avanzamento } from '@eventi/regole.ts';
import type { ProgrammaEvento } from '@eventi/tipi.ts';
import { cn } from '../../lib/cn';
import { coinvoltiNellaSessione, confermabili } from '../../lib/eventi/assegnazioni';
import { righePercorso } from '../../lib/eventi/programma';
import { ORDINE_STATI, STATI, cambiataDopoLaConferma, filtraPartecipanti, type StatoPartecipante } from '../../lib/eventi/stati';
import type { PartecipanteEvento } from '../../lib/eventi/tipi';
import { BadgeStato } from './BadgeStato';
import { Iniziali } from './Iniziali';
import { Pannello } from './Pannello';

type Filtro = StatoPartecipante | 'tutti' | 'incompleti';

type Props = {
  programma: ProgrammaEvento;
  partecipanti: PartecipanteEvento[];
  selezionati: Set<string>;
  onSelezione: (ids: Set<string>) => void;
  onModifica: (p: PartecipanteEvento) => void;
  /** Filtro dal tabellone: solo chi ha chiesto o ha questa sessione. */
  sessione: { codice: string; nome: string } | null;
  onTogliSessione: () => void;
};

/** Quanti turni assegnati sono diversi da quelli chiesti. */
function diversiDallaRichiesta(programma: ProgrammaEvento, p: PartecipanteEvento): number {
  if (!p.richiesta || p.assegnazione_stato === 'nessuna') return 0;
  const chiesto = righePercorso(programma, p.richiesta);
  const dato = righePercorso(programma, p.assegnazione);
  return chiesto.filter((r, i) => r.codice !== dato[i].codice).length;
}

/** L'elenco delle persone sotto il tabellone: si spuntano per confermarle, si apre una per modificarla. */
export function ElencoAssegnazioni({ programma, partecipanti, selezionati, onSelezione, onModifica, sessione, onTogliSessione }: Props) {
  const [filtro, setFiltro] = useState<Filtro>('tutti');
  const [testo, setTesto] = useState('');

  const visibili = useMemo(() => {
    const base = sessione ? coinvoltiNellaSessione(partecipanti, sessione.codice) : partecipanti;
    if (filtro === 'incompleti') {
      return filtraPartecipanti(base, { stato: 'bozza', testo }).filter((p) => !assegnazioneCompleta(programma, p.assegnazione));
    }
    return filtraPartecipanti(base, { stato: filtro, testo });
  }, [partecipanti, sessione, filtro, testo, programma]);

  const spuntabili = useMemo(() => new Set(confermabili(visibili).map((p) => p.id)), [visibili]);
  const tuttiSpuntati = spuntabili.size > 0 && [...spuntabili].every((id) => selezionati.has(id));

  const spunta = (id: string) => {
    const dopo = new Set(selezionati);
    if (dopo.has(id)) dopo.delete(id);
    else dopo.add(id);
    onSelezione(dopo);
  };
  const spuntaTutti = () => {
    const dopo = new Set(selezionati);
    for (const id of spuntabili) {
      if (tuttiSpuntati) dopo.delete(id);
      else dopo.add(id);
    }
    onSelezione(dopo);
  };

  return (
    <Pannello titolo="Persone" sotto="Spunta le bozze da confermare, oppure apri una persona per cambiarle il percorso." corpo="p-0 pt-3 md:p-0 md:pt-3">
      <div className="flex flex-col gap-2 px-4 pb-3 md:flex-row md:items-center md:px-5">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <Input value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="Cerca una persona" className="h-9 pl-9" />
        </div>
        <Select value={filtro} onValueChange={(v) => setFiltro(v as Filtro)}>
          <SelectTrigger className="h-9 md:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti</SelectItem>
            {ORDINE_STATI.map((s) => (
              <SelectItem key={s} value={s}>{STATI[s].plurale}</SelectItem>
            ))}
            <SelectItem value="incompleti">Bozze con turni vuoti</SelectItem>
          </SelectContent>
        </Select>
        {sessione && (
          <button type="button" onClick={onTogliSessione}
            className="inline-flex items-center gap-1 self-start rounded-full border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs text-primary-700 md:self-auto">
            {sessione.nome}
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 border-y border-gray-100 bg-gray-50/60 px-4 py-2 text-[11px] text-neutral-500 md:px-5">
        <input type="checkbox" checked={tuttiSpuntati} disabled={spuntabili.size === 0} onChange={spuntaTutti}
          aria-label="Seleziona tutte le bozze visibili" className="h-4 w-4 accent-primary-600" />
        <span>{visibili.length} persone · si possono spuntare solo le bozze con almeno un turno assegnato</span>
      </div>

      {visibili.length === 0 ? (
        <p className="py-10 text-center text-sm text-neutral-500">Nessuno corrisponde al filtro.</p>
      ) : (
        <ul className="max-h-[32rem] overflow-y-auto">
          {visibili.map((p) => {
            const { fatti, totale } = avanzamento(programma, p.assegnazione_stato === 'nessuna' ? null : p.assegnazione);
            const diversi = diversiDallaRichiesta(programma, p);
            const incompleto = fatti < totale && p.assegnazione_stato !== 'nessuna';
            return (
              <li key={p.id} className="flex items-center gap-3 border-t border-gray-100 px-4 py-2.5 first:border-t-0 md:px-5">
                <input type="checkbox" checked={selezionati.has(p.id)} disabled={!spuntabili.has(p.id)} onChange={() => spunta(p.id)}
                  aria-label={`Seleziona ${p.nome}`} className="h-4 w-4 accent-primary-600 disabled:opacity-30" />
                <Iniziali nome={p.nome} className="hidden sm:flex" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">{p.nome}</p>
                  <p className="flex flex-wrap items-center gap-x-2 text-[11px] text-neutral-500">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block h-1 w-10 overflow-hidden rounded-full bg-gray-200">
                        <span className={cn('block h-full', incompleto ? 'bg-amber-500' : 'bg-green-500')}
                          style={{ width: `${totale === 0 ? 0 : (fatti / totale) * 100}%` }} />
                      </span>
                      <span className={cn(incompleto && 'text-amber-700')}>{fatti}/{totale} turni</span>
                    </span>
                    {diversi > 0 && <span>· {diversi} diversi dalla richiesta</span>}
                    {cambiataDopoLaConferma(p) && <span className="text-amber-700">· cambiato dopo l'email</span>}
                  </p>
                </div>
                <BadgeStato partecipante={p} className="hidden sm:inline-flex" />
                <Button variant="outline" size="sm" onClick={() => onModifica(p)} className="flex-shrink-0">
                  <Pencil className="h-3.5 w-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Modifica</span>
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </Pannello>
  );
}
